import { format, isAfter, isToday } from 'date-fns'
import { TIME_OF_DAY, TTime } from './ModalSchedulePost'

const decodeHexColorToRGB = (string: string) => parseInt(string, 16)

export const convertHexToRGBA = (hexCode: string, opacity: number) => {
  let hex = hexCode.replace('#', '')

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }

  let r = decodeHexColorToRGB(hex.substring(0, 2))
  let g = decodeHexColorToRGB(hex.substring(2, 4))
  let b = decodeHexColorToRGB(hex.substring(4, 6))

  return `rgba(${r},${g},${b},${opacity})`
}

export const makeColorDarkIfItWhite = (hexCode: string) => {
  let hex = hexCode.replace('#', '')

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }

  let r = decodeHexColorToRGB(hex.substring(0, 2))
  let g = decodeHexColorToRGB(hex.substring(2, 4))
  let b = decodeHexColorToRGB(hex.substring(4, 6))

  if (r + g + b > 700) {
    r = r - 100
    g = g - 100
    b = b - 100
  }

  return `rgb(${r},${g},${b})`
}

export const formatDateToCellRefs = (date: Date) => format(new Date(date), 'yyyy-MM-dd')
export const formatDateMonth = (date: Date) => format(new Date(date), 'yyyy-MM')
export const dateIsAfter = (date: any) => isAfter(new Date(date), new Date())
export const dateIsToday = (date: any) => isToday(new Date(date))

export const transformEnglishTimeToGeneral24Time = ({ hours, minutes, timeOfDay }: TTime) => {
  const hoursInt = Number.parseInt(hours)
  if (timeOfDay === TIME_OF_DAY.PM) {
    if (hoursInt === 12) {
      return `${hours}:${minutes}`
    } else {
      const hoursInGeneralTime = 12 + hoursInt
      return `${hoursInGeneralTime}:${minutes}`
    }
  } else {
    if (hoursInt === 12) {
      return `00:${minutes}`
    } else {
      if (hoursInt > 9) {
        return `${hoursInt}:${minutes}`
      } else {
        return `0${hoursInt}:${minutes}`
      }
    }
  }
}

export const getHours12Clock = (date: Date) => format(date, 'h')
export const getMinutes12Clock = (date: Date) => format(date, 'mm')
export const getTimeOfDay12Clock = (date: Date) => format(date, 'aa')

interface IRefsHandler {
  [field: string]: any
}

const CLASS_EVENT_DRAGGING = 'event-is-dragging'
const CLASS_EMPTY_DAY = 'day-without-event'
const CLASS_NOT_EMPTY_DAY = 'day-with-event'

export class RefsHandler {
  dates: IRefsHandler
  constructor() {
    this.dates = {}
  }

  setDayCell = ({ date, el }: any) => {
    this.dates[formatDateToCellRefs(date)] = {
      DOMelement: el,
      events: {}
    }
  }

  setEventToCell = (event: any, el: any) => {
    this.dates[formatDateToCellRefs(event.start)].DOMelement.classList.remove(CLASS_EMPTY_DAY)
    this.dates[formatDateToCellRefs(event.start)].DOMelement.classList.add(CLASS_NOT_EMPTY_DAY)
    this.dates[formatDateToCellRefs(event.start)].events[event.id] = {
      DOMelement: el,
      event
    }
  }

  setDraggingEvent = ({ start, id }: any) => {
    // not show dragg effect for not allowed events
    if (dateIsAfter(start)) {
      this.dates[formatDateToCellRefs(start)].events[id].DOMelement.classList.add(
        CLASS_EVENT_DRAGGING
      )
    }
  }

  removeDraggingEvent = ({ start, id, ...rest }: any) => {
    this.dates[formatDateToCellRefs(start)].events[id].DOMelement.classList.remove(
      CLASS_EVENT_DRAGGING
    )
  }

  deleteEventRefs = () => {
    for (let date of Object.keys(this.dates)) {
      this.dates[date].events = {}
      this.dates[date].label = {}
      if (dateIsAfter(date) || dateIsToday(date)) {
        this.dates[date].DOMelement.classList.add(CLASS_EMPTY_DAY)
      }
      this.dates[date].DOMelement.classList.remove(CLASS_NOT_EMPTY_DAY)
    }
  }

  isHasContent = (date: any) => {
    const { label, events } = this.dates[formatDateToCellRefs(date)]
    return !!Object.keys(events).length || (!!label && !!Object.keys(label).length)
  }

  transformLabelsToCalendar = (labels: any[]) => {
    return labels.map((label) => ({
      ...label,
      textColor: label.color,
      backgroundColor: convertHexToRGBA(label.color, 0.0),
      borderColor: convertHexToRGBA(label.color, 0.0),
      start: new Date(label.start_at),
      end: new Date(label.end_at),
      startEditable: false,
      isLabel: true
    }))
  }

  transformPostsToCalendar = (posts: any[]) => {
    return posts.map((post) => {
      const publishDate = new Date(post.date + 'T' + post.time)
      return {
        schedule: {
          ...post,
          start: post.date + 'T' + post.time
        },
        id: post.id,
        start: publishDate,
        end: publishDate,
        startEditable: dateIsAfter(publishDate),
        isPost: true
      }
    })
  }

  setLabelToCell = (date: any, el: any) => {
    if (!this.dates[formatDateToCellRefs(date)]) return
    this.dates[formatDateToCellRefs(date)].DOMelement.classList.remove(CLASS_EMPTY_DAY)
    this.dates[formatDateToCellRefs(date)].DOMelement.classList.add(CLASS_NOT_EMPTY_DAY)
    this.dates[formatDateToCellRefs(date)].label = {
      DOMelement: el
    }
  }
}
